import React from "react";
import { Globe, Edit3, Sparkles } from "lucide-react";
import { motion } from "framer-motion";

const MoreFeatures = () => {
  // Languages for scrolling animation
  const languagesRow1 = ["English", "Spanish", "French", "German", "Italian", "Portuguese", "Russian", "Japanese"];
  const languagesRow2 = ["Chinese", "Korean", "Arabic", "Hindi", "Turkish", "Polish", "Dutch", "Swedish"];
  const languagesRow3 = ["Norwegian", "Danish", "Finnish", "Greek", "Hebrew", "Thai", "Vietnamese", "Indonesian"];

  // Keywords for floating animation
  const keywords = [
    { text: "SEO Tips", delay: 0 },
    { text: "Content Marketing", delay: 0.2 },
    { text: "Link Building", delay: 0.4 },
    { text: "Keyword Research", delay: 0.6 },
    { text: "Digital Strategy", delay: 0.8 },
    { text: "Blog Growth", delay: 1.0 },
    { text: "Organic Traffic", delay: 1.2 },
    { text: "SERP Ranking", delay: 1.4 },
  ];

  return (
    <section className="py-10 md:py-10 bg-gray-50">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4 leading-tight">
            And so{" "}
            <span className="text-green-500">much more feautures</span>{" "}
          </h2>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-700">
            to rank your website higher.
          </h2>
        </div>

        {/* Three Cards */}
        <div className="grid md:grid-cols-3 gap-8">
          {/* Card 1: Multi-Language */}
          <div className="bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-200">
            {/* Visual Mockup Area - Scrolling Languages */}
            <div className="bg-gradient-to-br from-green-50 to-green-100 flex flex-col items-center justify-center min-h-[280px] overflow-hidden py-8 space-y-3">
              {/* Row 1 - Scroll Left */}
              <div className="w-full overflow-hidden">
                <motion.div
                  className="flex gap-2"
                  animate={{
                    x: [0, -400],
                  }}
                  transition={{
                    duration: 15,
                    repeat: Infinity,
                    ease: "linear",
                  }}
                >
                  {[...languagesRow1, ...languagesRow1].map((lang, index) => (
                    <div
                      key={index}
                      className="px-4 py-2 bg-white rounded-full border-2 border-green-500 shadow-sm whitespace-nowrap"
                    >
                      <span className="text-xs font-semibold text-gray-700">{lang}</span>
                    </div>
                  ))}
                </motion.div>
              </div>

              {/* Row 2 - Scroll Right */}
              <div className="w-full overflow-hidden">
                <motion.div
                  className="flex gap-2"
                  animate={{
                    x: [-400, 0],
                  }}
                  transition={{
                    duration: 18,
                    repeat: Infinity,
                    ease: "linear",
                  }}
                >
                  {[...languagesRow2, ...languagesRow2].map((lang, index) => (
                    <div
                      key={index}
                      className="px-4 py-2 bg-white rounded-full border-2 border-green-500 shadow-sm whitespace-nowrap"
                    >
                      <span className="text-xs font-semibold text-gray-700">{lang}</span>
                    </div>
                  ))}
                </motion.div>
              </div>

              {/* Row 3 - Scroll Left */}
              <div className="w-full overflow-hidden">
                <motion.div
                  className="flex gap-2"
                  animate={{
                    x: [0, -400],
                  }}
                  transition={{
                    duration: 20,
                    repeat: Infinity,
                    ease: "linear",
                  }}
                >
                  {[...languagesRow3, ...languagesRow3].map((lang, index) => (
                    <div
                      key={index}
                      className="px-4 py-2 bg-white rounded-full border-2 border-green-500 shadow-sm whitespace-nowrap"
                    >
                      <span className="text-xs font-semibold text-gray-700">{lang}</span>
                    </div>
                  ))}
                </motion.div>
              </div>
            </div>

            {/* Card Content */}
            <div className="p-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="bg-gradient-to-br from-green-400 to-green-500 rounded-full w-10 h-10 flex items-center justify-center shadow-md">
                  <Globe className="h-5 w-5 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">
                  Write in your language
                </h3>
              </div>
              <p className="text-gray-600 leading-relaxed">
                Generate high-quality content in over <span className="font-bold text-green-600">150+ languages</span>. 
                Our AI creates articles in any language you need.
              </p>
            </div>
          </div>

          {/* Card 2: AI Editor */}
          <div className="bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-200">
            {/* Visual Mockup Area - AI Editing Animation */}
            <div className="bg-gradient-to-br from-green-50 to-green-100 p-8 flex items-center justify-center min-h-[280px]">
              <div className="w-full max-w-[280px] h-[240px] bg-white rounded-xl shadow-lg border-2 border-green-500 p-4 overflow-hidden relative">
                {/* Editor Header */}
                <div className="flex items-center gap-2 mb-3 pb-2 border-b border-gray-200">
                  <motion.div
                    animate={{ rotate: [0, 360] }}
                    transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                  >
                    <Sparkles className="h-4 w-4 text-green-500" />
                  </motion.div>
                  <span className="text-[10px] font-semibold text-green-500">AI Editor Active</span>
                </div>

                {/* Text Content with Highlights */}
                <div className="space-y-2">
                  {/* Line 1 - Original */}
                  <motion.div
                    className="text-[10px] text-gray-600 leading-relaxed"
                    initial={{ opacity: 1 }}
                  >
                    <span>Improve your website's search engine</span>
                  </motion.div>

                  {/* Line 2 - Being Edited */}
                  <motion.div className="relative">
                    <motion.div
                      className="absolute inset-0 bg-yellow-200 rounded"
                      initial={{ width: 0 }}
                      animate={{ width: "100%" }}
                      transition={{
                        delay: 0.5,
                        duration: 0.5,
                        repeat: Infinity,
                        repeatDelay: 2.5,
                      }}
                    />
                    <motion.div
                      className="text-[10px] text-gray-600 leading-relaxed relative z-10"
                      animate={{ opacity: [1, 0.7, 1] }}
                      transition={{
                        delay: 1,
                        duration: 1,
                        repeat: Infinity,
                        repeatDelay: 2,
                      }}
                    >
                      <motion.span
                        initial={{ opacity: 1 }}
                        animate={{ opacity: [1, 0, 0, 1] }}
                        transition={{
                          delay: 1.2,
                          duration: 1.5,
                          repeat: Infinity,
                          repeatDelay: 2,
                        }}
                      >
                        rankings with good content strategies
                      </motion.span>
                      <motion.span
                        className="text-green-600 font-semibold"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: [0, 0, 1, 1] }}
                        transition={{
                          delay: 1.2,
                          duration: 1.5,
                          repeat: Infinity,
                          repeatDelay: 2,
                        }}
                      >
                        rankings with proven SEO strategies
                      </motion.span>
                    </motion.div>
                  </motion.div>

                  {/* Line 3 - Original */}
                  <div className="text-[10px] text-gray-600 leading-relaxed">
                    <span>that drive organic traffic and engagement.</span>
                  </div>

                  {/* AI Suggestion Popup */}
                  <motion.div
                    className="absolute bottom-4 left-4 right-4 bg-white border-2 border-green-500 rounded-lg p-2 shadow-xl"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: [0, 0, 1, 1, 0] }}
                    transition={{
                      delay: 1.5,
                      duration: 2.5,
                      repeat: Infinity,
                      repeatDelay: 1.5,
                    }}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <Sparkles className="h-3 w-3 text-green-500" />
                      <span className="text-[9px] font-semibold text-green-600">AI Suggestion</span>
                    </div>
                    <p className="text-[8px] text-gray-600 mb-1">
                      Replace "good content" with "proven SEO strategies" for stronger impact
                    </p>
                    <motion.button
                      className="text-[8px] font-semibold text-green-600 bg-green-50 px-2 py-0.5 rounded"
                      whileHover={{ scale: 1.05 }}
                    >
                      Apply
                    </motion.button>
                  </motion.div>
                </div>
              </div>
            </div>

            {/* Card Content */}
            <div className="p-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="bg-gradient-to-br from-green-400 to-green-500 rounded-full w-10 h-10 flex items-center justify-center shadow-md">
                  <Edit3 className="h-5 w-5 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">
                  Edit article with AI
                </h3>
              </div>
              <p className="text-gray-600 leading-relaxed">
                <span className="font-bold text-green-600">Easily edit your article</span> to meet your exact expectations, 
                ensuring every word aligns with your vision for standout content.
              </p>
            </div>
          </div>

          {/* Card 3: Unlimited Keywords */}
          <div className="bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-200">
            {/* Visual Mockup Area - Keyword Generator Stream */}
            <div className="bg-gradient-to-br from-green-50 to-green-100 p-8 flex items-center justify-center min-h-[280px] overflow-hidden relative">
              <div className="w-full max-w-[280px] h-[240px] bg-white rounded-xl shadow-lg border-2 border-green-500 overflow-hidden relative">
                {/* Header with generating indicator */}
                <div className="absolute top-0 left-0 right-0 bg-gradient-to-b from-green-500 to-green-600 px-4 py-2 z-10">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <motion.div
                        animate={{ rotate: 360 }}
                        transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                      >
                        <Sparkles className="h-3 w-3 text-white" />
                      </motion.div>
                      <span className="text-[10px] font-semibold text-white">Generating Keywords...</span>
                    </div>
                    <motion.div
                      className="text-[10px] font-bold text-white"
                      animate={{ opacity: [0.5, 1, 0.5] }}
                      transition={{ duration: 1.5, repeat: Infinity }}
                    >
                      ∞
                    </motion.div>
                  </div>
                </div>

                {/* Scrolling keywords feed */}
                <div className="pt-10 h-full overflow-hidden">
                  <motion.div
                    animate={{
                      y: [0, -800],
                    }}
                    transition={{
                      duration: 15,
                      repeat: Infinity,
                      ease: "linear",
                    }}
                  >
                    {[...keywords, ...keywords, ...keywords].map((keyword, index) => (
                      <motion.div
                        key={index}
                        className="px-4 py-2 border-b border-gray-100"
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{
                          delay: (index % keywords.length) * 0.3,
                          duration: 0.5,
                        }}
                      >
                        <div className="flex items-center gap-2">
                          <motion.div
                            className="w-1.5 h-1.5 rounded-full bg-green-500"
                            animate={{
                              scale: [1, 1.5, 1],
                              opacity: [0.5, 1, 0.5],
                            }}
                            transition={{
                              duration: 1,
                              repeat: Infinity,
                              delay: (index % keywords.length) * 0.1,
                            }}
                          />
                          <span className="text-xs text-gray-700">{keyword.text}</span>
                          <motion.div
                            className="ml-auto"
                            initial={{ opacity: 0, scale: 0 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{
                              delay: (index % keywords.length) * 0.3 + 0.2,
                              duration: 0.3,
                            }}
                          >
                            <div className="px-2 py-0.5 bg-green-100 rounded-full">
                              <span className="text-[8px] font-semibold text-green-700">NEW</span>
                            </div>
                          </motion.div>
                        </div>
                        
                        {/* Keyword metrics */}
                        <motion.div
                          className="flex gap-3 mt-1 ml-3.5"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{
                            delay: (index % keywords.length) * 0.3 + 0.3,
                            duration: 0.3,
                          }}
                        >
                          <div className="flex items-center gap-1">
                            <div className="w-1 h-1 rounded-full bg-green-400" />
                            <span className="text-[8px] text-gray-500">Vol: {Math.floor(Math.random() * 5000 + 1000)}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <div className="w-1 h-1 rounded-full bg-green-400" />
                            <span className="text-[8px] text-gray-500">Diff: {Math.floor(Math.random() * 50 + 10)}</span>
                          </div>
                        </motion.div>
                      </motion.div>
                    ))}
                  </motion.div>
                </div>

                {/* Bottom fade gradient */}
                <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-white to-transparent pointer-events-none" />
                
                {/* Particle effects */}
                {[...Array(6)].map((_, i) => (
                  <motion.div
                    key={`particle-${i}`}
                    className="absolute w-1 h-1 bg-green-400 rounded-full"
                    style={{
                      left: `${20 + i * 15}%`,
                      top: '50%',
                    }}
                    animate={{
                      y: [-20, 20],
                      opacity: [0, 1, 0],
                    }}
                    transition={{
                      duration: 2,
                      delay: i * 0.3,
                      repeat: Infinity,
                      ease: "easeInOut",
                    }}
                  />
                ))}
              </div>
            </div>

            {/* Card Content */}
            <div className="p-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="bg-gradient-to-br from-green-400 to-green-500 rounded-full w-10 h-10 flex items-center justify-center shadow-md">
                  <Sparkles className="h-5 w-5 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">
                  Generate unlimited keywords
                </h3>
              </div>
              <p className="text-gray-600 leading-relaxed">
                Generate <span className="font-bold text-green-600">unlimited sets of keywords</span> until you find the 
                perfect match for your content strategy.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default MoreFeatures;

